import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
    FileText, RefreshCw, Loader2, AlertTriangle, TrendingDown, Users,
    Lightbulb, AlertOctagon, ChevronDown, ChevronUp,
} from 'lucide-react';

function relativeTime(iso) {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const diff = Date.now() - t;
    const m = Math.round(diff / 60000);
    if (m < 60) return `hace ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    return `hace ${d} d`;
}

const STAGE_LABELS_SHORT = {
    registrados: 'Registrado',
    borradores_opened: 'Entró a Borradores',
    new_draft_clicked: 'Clic Nuevo Borrador',
    borrador_indicador_selected: 'Seleccionó Indicador',
    borrador_diagnostico_viewed: 'Vio Diagnóstico',
    borrador_continuar_clicked: 'Continuó al editor',
};

export default function BorradoresFunnelPanel() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [days, setDays] = useState('30');
    const [expandStuck, setExpandStuck] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/analytics/borradores-funnel?days=${days}`);
            setData(data);
        } catch {
            toast.error('No se pudo cargar el embudo de Borradores');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading && !data) {
        return (
            <Card data-testid="borradores-funnel-panel" id="borradores-funnel">
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Embudo de Borradores
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data) return null;

    const funnel = data.funnel;
    const base = funnel[0]?.count || 0;
    const finalCount = funnel[funnel.length - 1]?.count || 0;
    const globalConv = base > 0 ? ((finalCount / base) * 100).toFixed(1) : '0.0';

    return (
        <Card data-testid="borradores-funnel-panel" id="borradores-funnel">
            <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Embudo de Borradores
                            <Badge className="bg-rose-100 text-rose-700 text-[10px]">Crítico</Badge>
                        </CardTitle>
                        <CardDescription>
                            Punto de entrada real al Portafolio. Conversión global Registrado → Primer Borrador:{' '}
                            <strong className={cn(parseFloat(globalConv) >= 30 ? 'text-emerald-700' : 'text-rose-700')}>
                                {globalConv}%
                            </strong>{' '}
                            · Período: {data.period.start} → {data.period.end}
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={days} onValueChange={setDays}>
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid="borradores-funnel-days">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">Últimos 7 días</SelectItem>
                                <SelectItem value="14">Últimos 14 días</SelectItem>
                                <SelectItem value="30">Últimos 30 días</SelectItem>
                                <SelectItem value="60">Últimos 60 días</SelectItem>
                                <SelectItem value="90">Últimos 90 días</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} className="gap-1.5" data-testid="borradores-funnel-refresh">
                            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Embudo visual */}
                <div className="space-y-1.5" data-testid="borradores-funnel-rows">
                    {funnel.map((s, i) => {
                        const widthPct = base > 0 ? Math.max(8, (s.count / base) * 100) : 8;
                        const isBiggestDrop = data.biggest_drop && data.biggest_drop.to_stage === s.stage;
                        return (
                            <div
                                key={s.stage}
                                className={cn(
                                    'rounded-lg border bg-white p-3 transition-colors',
                                    isBiggestDrop ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'
                                )}
                                data-testid={`borradores-funnel-stage-${s.stage}`}
                            >
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <span className="font-mono text-[10px] text-slate-400">{i + 1}</span>
                                        <span className="text-sm font-medium text-slate-900 truncate">{s.label}</span>
                                        {isBiggestDrop && (
                                            <Badge className="bg-rose-500 text-white text-[9px] px-1.5 py-0">Mayor fuga</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="font-mono text-base font-bold text-slate-900 tabular-nums">{s.count}</span>
                                        {i > 0 && s.conv_vs_prev != null && (
                                            <span className="text-[10px] text-slate-500 font-mono w-12 text-right tabular-nums" title="Conversión vs. etapa anterior">
                                                {s.conv_vs_prev}%
                                            </span>
                                        )}
                                        {i > 0 && s.drop_pct != null && (
                                            <span
                                                className={cn(
                                                    'text-[10px] font-mono w-14 text-right tabular-nums',
                                                    s.drop_pct > 50 ? 'text-rose-700 font-semibold' : 'text-slate-400'
                                                )}
                                                title="Drop% desde etapa anterior"
                                            >
                                                -{s.drop_pct}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className={cn(
                                            'h-full rounded-full transition-all',
                                            isBiggestDrop ? 'bg-rose-400' : 'bg-slate-700'
                                        )}
                                        style={{ width: `${widthPct}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[10px] text-slate-400">% Conversión vs. etapa anterior · % Caída desde etapa anterior</p>

                {/* Insights automáticos */}
                {data.insights.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="borradores-insights">
                        <p className="font-heading text-sm font-bold text-amber-900 flex items-center gap-2">
                            <Lightbulb className="h-4 w-4" />
                            Insights del embudo
                        </p>
                        <ul className="space-y-1.5">
                            {data.insights.map((ins, i) => (
                                <li key={i} className="text-xs text-amber-900 flex gap-2">
                                    <span className="text-amber-600">→</span>
                                    <span>{ins}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Punto de mayor fuga */}
                {data.biggest_drop && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4" data-testid="borradores-biggest-drop">
                        <p className="font-heading text-sm font-bold text-rose-900 flex items-center gap-1.5">
                            <TrendingDown className="h-4 w-4" />
                            Principal punto de fuga
                        </p>
                        <p className="mt-1 text-sm text-rose-800">
                            Entre <strong>{data.biggest_drop.from_label}</strong> y <strong>{data.biggest_drop.to_label}</strong>:{' '}
                            <span className="font-mono font-semibold">{data.biggest_drop.drop_pct}%</span> de abandono
                            {' '}(<strong>{data.biggest_drop.lost_count}</strong> docentes perdidos).
                        </p>
                    </div>
                )}

                {/* Errores técnicos en creación */}
                {data.failed_attempts && data.failed_attempts.length > 0 && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4" data-testid="borradores-errors">
                        <p className="font-heading text-sm font-bold text-rose-900 mb-2 flex items-center gap-1.5">
                            <AlertOctagon className="h-4 w-4" />
                            Intentos de creación fallidos ({data.failed_attempts.length})
                        </p>
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                            {data.failed_attempts.slice(0, 10).map((f, i) => (
                                <li key={i} className="text-xs text-rose-800">
                                    <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">{f.last_action || '?'}</code>{' '}
                                    {f.error || 'sin detalle'} · <span className="text-slate-500">{relativeTime(f.ts)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Lista de usuarios atascados */}
                <div className="rounded-xl border border-slate-200 bg-white">
                    <button
                        type="button"
                        onClick={() => setExpandStuck(!expandStuck)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                        data-testid="borradores-stuck-toggle"
                    >
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <p className="font-heading text-sm font-bold text-slate-900">
                                Docentes registrados que NO han creado un borrador
                            </p>
                            <Badge className="bg-rose-100 text-rose-700 text-[10px]">
                                {data.stuck_total}
                            </Badge>
                        </div>
                        {expandStuck ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </button>
                    {expandStuck && (
                        <div className="border-t border-slate-100 max-h-96 overflow-y-auto" data-testid="borradores-stuck-list">
                            {data.stuck_users.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    ✨ Todos los registrados han creado al menos un borrador.
                                </p>
                            ) : (
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 sticky top-0">
                                        <tr className="text-left">
                                            <th className="px-3 py-2 font-semibold text-slate-600">Docente</th>
                                            <th className="px-3 py-2 font-semibold text-slate-600">Etapa de abandono</th>
                                            <th className="px-3 py-2 font-semibold text-slate-600">Registrado</th>
                                            <th className="px-3 py-2 font-semibold text-slate-600">Posible causa</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.stuck_users.map((u) => (
                                            <tr
                                                key={u.user_id}
                                                className="border-t border-slate-100"
                                                data-testid={`borradores-stuck-user-${u.user_id}`}
                                            >
                                                <td className="px-3 py-2">
                                                    <p className="font-medium text-slate-900">{u.nombre || u.email}</p>
                                                    <p className="text-[10px] text-slate-500">{u.email}</p>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Badge className="bg-amber-100 text-amber-800 text-[9px]">
                                                        {STAGE_LABELS_SHORT[u.abandono_stage] || u.abandono_label}
                                                    </Badge>
                                                </td>
                                                <td className="px-3 py-2 text-slate-500">
                                                    {relativeTime(u.registered_at)}
                                                </td>
                                                <td className="px-3 py-2 text-slate-600 italic max-w-xs">
                                                    {u.causa_posible}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>

                {/* Resumen para tomar acción */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-heading text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                        <Users className="h-3.5 w-3.5" />
                        Qué hacer
                    </p>
                    <ul className="space-y-1 text-xs text-slate-600 leading-relaxed">
                        <li>· Revisa la <strong>etapa con mayor fuga</strong> para entender dónde se pierden los docentes.</li>
                        <li>· Considera enviar un <strong>correo de reactivación</strong> a los {data.stuck_total} docentes atascados.</li>
                        <li>· Si los <em>insights</em> mencionan combinaciones sin diagnóstico, verifica las tablas de datos nacionales.</li>
                        <li>· Los datos se acumulan desde la activación del tracking — para histórico completo, espera al menos 7 días.</li>
                    </ul>
                </div>
            </CardContent>
        </Card>
    );
}
